import React, { useState, useEffect } from "react";
import Modal from "@components/modal";
import Loader from "@components/loader";
import Pagination from "@components/pagination";
import useConfig from "@hooks/useConfig";
import { Product } from "@apis/index";
import { Trash2, Plus, ChevronRight } from "lucide-react";

interface Props {
  product: any;
  visible: boolean;
  onClose: () => void;
}

const RecommendationModal: React.FC<Props> = ({
  product,
  visible,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"current" | "browse">("current");
  const [recommendations, setRecommendations] = useState<any[]>([]);

  // Browse State
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>(null);

  const [candidates, setCandidates] = useState<any>({
    items: [],
    totalPages: 0,
  });

  const [loading, setLoading] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [page, setPage] = useState(1);
  const { bucketUrl } = useConfig();

  // Initial Fetch
  useEffect(() => {
    if (visible && product?.id) {
      if (activeTab === "current") {
        fetchRecommendations();
      } else {
        // Load categories from product data
        if (product.category) {
          setCategories([product.category]);
        }
      }
    }
  }, [visible, product, activeTab]);

  // Fetch Candidates when filtering changes
  useEffect(() => {
    if (activeTab === "browse" && visible && product?.id) {
      if (selectedCategory) {
        fetchCandidates();
      } else {
        setCandidates({ items: [], totalPages: 0 });
      }
    }
  }, [
    activeTab,
    visible,
    product,
    selectedCategory,
    selectedSubCategory,
    page,
  ]);

  // Fetch Subcategories when Category changes
  useEffect(() => {
    if (selectedCategory) {
      // Use product's subcategories instead of API fetch
      setSubCategories(product.subCategories || []);
      setSelectedSubCategory(null); // Reset subcat
    } else {
      setSubCategories([]);
    }
  }, [selectedCategory]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const res: any = await Product.getRecommendations({ id: product.id });
      setRecommendations(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Removed fetchCategories and fetchSubCategories API calls as we use local data

  const fetchCandidates = async () => {
    setLoadingCandidates(true);
    try {
      const params: any = {
        page,
        limit: 10,
        categoryId: selectedCategory?.id,
        isActive: true,
      };
      if (selectedSubCategory?.id) {
        params.subCategoryId = selectedSubCategory.id;
      }

      const res: any = await Product.getRecommendationCandidates({
        id: product.id,
        params,
      });
      setCandidates(res || { items: [], totalPages: 0 });
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleAdd = async (item: any) => {
    // Optimistically update UI
    setRecommendations((prev) => [...prev, item]);

    try {
      await Product.addRecommendation({
        id: product.id,
        data: { recommendedProductId: item.id },
      });
      await fetchRecommendations();
    } catch (err) {
      console.error(err);
      await fetchRecommendations();
    }
  };

  const handleRemove = async (recommendedProductId: number) => {
    setRecommendations((prev) =>
      prev.filter((r) => r.id !== recommendedProductId),
    );
    try {
      await Product.removeRecommendation({
        id: product.id,
        data: { recommendedProductId },
      });
      await fetchRecommendations();
    } catch (err) {
      console.error(err);
      await fetchRecommendations();
    }
  };

  const isRecommended = (id: number) => {
    return recommendations.some((r) => r.id === id);
  };

  const renderProductList = (items: any[], type: "current" | "browse") => {
    if (!items?.length)
      return (
        <div
          style={{ textAlign: "center", color: "#888", padding: "40px 20px" }}
        >
          {type === "browse"
            ? selectedCategory
              ? "No products found in this category."
              : "Select a category to browse products."
            : "No recommendations added."}
        </div>
      );

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {items.map((item) => {
          const added = isRecommended(item.id);
          return (
            <div
              key={item.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                border: "1px solid #f0f0f0",
                borderRadius: "8px",
                background: "#fff",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <img
                  src={
                    item.images?.[0]?.image
                      ? `${bucketUrl}/${item.images[0].image}`
                      : "https://via.placeholder.com/40"
                  }
                  alt={item.name}
                  style={{
                    width: "40px",
                    height: "40px",
                    objectFit: "cover",
                    borderRadius: "6px",
                    backgroundColor: "#f7fafc",
                  }}
                />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#333",
                    }}
                  >
                    {item.name}
                  </span>
                  <span style={{ fontSize: "12px", color: "#718096" }}>
                    Price: {item.price}
                  </span>
                </div>
              </div>
              <div>
                {type === "current" ? (
                  <button
                    onClick={() => handleRemove(item.id)}
                    style={{
                      border: "1px solid #fed7d7",
                      background: "#fff5f5",
                      cursor: "pointer",
                      color: "#c53030",
                      padding: "6px",
                      borderRadius: "6px",
                      display: "flex",
                    }}
                    title="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                ) : (
                  <button
                    onClick={() =>
                      added ? handleRemove(item.id) : handleAdd(item)
                    }
                    style={{
                      border: added ? "1px solid #e2e8f0" : "none",
                      background: added ? "white" : "#ebf8ff",
                      color: added ? "#e53e3e" : "#3182ce",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {added ? (
                      <>
                        <Trash2 size={14} /> Remove
                      </>
                    ) : (
                      <>
                        <Plus size={14} /> Add
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      style={{
        padding: "0 20px 20px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <h3
          style={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#2d3748",
            margin: 0,
          }}
        >
          Recommendations for {product?.name}
        </h3>
        <span style={{ fontSize: "13px", color: "#718096" }}>
          Select related products to recommend.
        </span>
      </div>

      {/* Top Tabs */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          background: "#f1f5f9",
          padding: "4px",
          borderRadius: "8px",
        }}
      >
        <button
          onClick={() => setActiveTab("current")}
          style={{
            flex: 1,
            padding: "8px",
            borderRadius: "6px",
            border: "none",
            background: activeTab === "current" ? "white" : "transparent",
            color: activeTab === "current" ? "#333" : "#64748b",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            boxShadow:
              activeTab === "current" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
          }}
        >
          Current ({recommendations.length})
        </button>
        <button
          onClick={() => setActiveTab("browse")}
          style={{
            flex: 1,
            padding: "8px",
            borderRadius: "6px",
            border: "none",
            background: activeTab === "browse" ? "white" : "transparent",
            color: activeTab === "browse" ? "#333" : "#64748b",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            boxShadow:
              activeTab === "browse" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
          }}
        >
          Browse Catalog
        </button>
      </div>

      {/* Content Area */}
      <div
        style={{
          minHeight: "300px",
          maxHeight: "50vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {activeTab === "current" &&
          (loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "40px",
              }}
            >
              <Loader />
            </div>
          ) : (
            renderProductList(recommendations, "current")
          ))}

        {activeTab === "browse" && (
          <>
            {/* Categories */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  color: "#a0aec0",
                }}
              >
                Categories
              </span>
              <div
                style={{
                  display: "flex",
                  overflowX: "auto",
                  gap: "8px",
                  paddingBottom: "4px",
                  scrollbarWidth: "none",
                }}
              >
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      whiteSpace: "nowrap",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      border:
                        selectedCategory?.id === cat.id
                          ? "1px solid #4299e1"
                          : "1px solid #e2e8f0",
                      background:
                        selectedCategory?.id === cat.id ? "#ebf8ff" : "white",
                      color:
                        selectedCategory?.id === cat.id ? "#2b6cb0" : "#4a5568",
                      fontSize: "13px",
                      cursor: "pointer",
                      fontWeight: selectedCategory?.id === cat.id ? 500 : 400,
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Subcategories (if any) */}
            {selectedCategory && subCategories.length > 0 && (
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "#a0aec0",
                  }}
                >
                  Sub-Categories
                </span>
                <div
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    gap: "8px",
                    paddingBottom: "4px",
                    scrollbarWidth: "none",
                  }}
                >
                  <button
                    onClick={() => setSelectedSubCategory(null)}
                    style={{
                      whiteSpace: "nowrap",
                      padding: "6px 12px",
                      borderRadius: "20px",
                      border: !selectedSubCategory
                        ? "1px solid #4299e1"
                        : "1px solid #e2e8f0",
                      background: !selectedSubCategory ? "#ebf8ff" : "white",
                      color: !selectedSubCategory ? "#2b6cb0" : "#4a5568",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    All
                  </button>
                  {subCategories.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => setSelectedSubCategory(sub)}
                      style={{
                        whiteSpace: "nowrap",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        border:
                          selectedSubCategory?.id === sub.id
                            ? "1px solid #4299e1"
                            : "1px solid #e2e8f0",
                        background:
                          selectedSubCategory?.id === sub.id
                            ? "#ebf8ff"
                            : "white",
                        color:
                          selectedSubCategory?.id === sub.id
                            ? "#2b6cb0"
                            : "#4a5568",
                        fontSize: "13px",
                        cursor: "pointer",
                        fontWeight:
                          selectedSubCategory?.id === sub.id ? 500 : 400,
                      }}
                    >
                      {sub.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Product List */}
            <div style={{ marginTop: "8px" }}>
              {loadingCandidates ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "40px",
                  }}
                >
                  <Loader />
                </div>
              ) : (
                renderProductList(candidates.items, "browse")
              )}
              {candidates?.totalPages > 1 && (
                <div style={{ marginTop: "16px" }}>
                  <Pagination
                    totalPages={candidates.totalPages}
                    currentPage={page}
                    setPage={setPage}
                    totalItems={candidates.totalItems}
                    isShown={true}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecommendationModal;
